// Deletion + crypto-shredding (Tool Plan §9.3; Implementation Plan v02 §6.2,
// D-78; key-custody.md; I-1a Batch 3).
//
// - Delete one moment: the moment, every record linked to it (answers, its
//   activity events, a draft) and its photo blobs go in ONE IndexedDB
//   transaction — all or nothing. A photo's key lives inside the moment record,
//   so the photo is unreadable the moment that record is gone. ("Deleted · Undo"
//   is a screen-level delay before this runs; the activity log's hash chain is
//   re-sealed by activity/ when that module lands.)
// - Delete everything: the keyring (every wrap of the data key) is cleared and
//   COMMITTED FIRST — crypto-shredding — then every other store, then the
//   database itself. Strong combined with real deletion, not a legal guarantee on
//   its own, and it cannot reach files the person already exported (IR-24).
//
// Per-moment keys are deferred to v1.1 (Batch 3 decision 3): on the phone the
// browser does not promise to overwrite deleted bytes, and a clear key pointer
// would show which records belong together.

import { deleteDB } from 'idb'

import type { DiaryRecord } from './codec'
import { DB_NAME, openStoreDb, type StoreDb, watchDone } from './schema'

export interface MomentGroup {
  readonly recordIds: readonly string[]
  readonly blobIds: readonly string[]
}

/** The moment + everything linked to it, from the in-memory index. */
export function collectMomentGroup(index: ReadonlyMap<string, DiaryRecord>, momentId: string): MomentGroup {
  const recordIds: string[] = []
  const blobIds = new Set<string>()
  for (const record of index.values()) {
    if (record.id === momentId || record.links?.moment === momentId) {
      recordIds.push(record.id)
      for (const blobId of record.links?.blobs ?? []) {
        blobIds.add(blobId)
      }
    }
  }
  return { recordIds, blobIds: [...blobIds] }
}

export async function deleteGroup(db: StoreDb, group: MomentGroup): Promise<void> {
  const tx = db.transaction(['records', 'blobs'], 'readwrite')
  const done = watchDone(tx)
  const records = tx.objectStore('records')
  const blobs = tx.objectStore('blobs')
  await Promise.all([...group.recordIds.map((id) => records.delete(id)), ...group.blobIds.map((id) => blobs.delete(id))])
  await done
}

/**
 * Keys first (committed), then content, then the database. ALWAYS closes `db`,
 * even when a step fails, so it can be retried (e.g. with destroyStore).
 * `onBlocked` fires if another open copy holds the database (the screen can ask
 * the person to close the other window); copies of this code close themselves
 * on `blocking`, so this normally resolves at once.
 */
export async function destroyEverything(db: StoreDb, dbName: string, onBlocked?: () => void): Promise<void> {
  try {
    const keysTx = db.transaction('keyring', 'readwrite')
    const keysDone = watchDone(keysTx)
    await keysTx.store.clear()
    await keysDone

    const tx = db.transaction(['records', 'blobs', 'premigration', 'meta'], 'readwrite')
    const done = watchDone(tx)
    await Promise.all([
      tx.objectStore('records').clear(),
      tx.objectStore('blobs').clear(),
      tx.objectStore('premigration').clear(),
      tx.objectStore('meta').clear(),
    ])
    await done
  } finally {
    db.close()
  }
  await deleteDB(dbName, onBlocked ? { blocked: onBlocked } : {})
}

/**
 * "Delete everything" WITHOUT the data key — for "I can't open it" (forgotten
 * secret, or a store that will not open). Needs no unlock and no open store.
 */
export async function destroyStore(dbName: string = DB_NAME, onBlocked?: () => void): Promise<void> {
  const db = await openStoreDb(dbName)
  await destroyEverything(db, dbName, onBlocked)
}
